// ============================================================
// A QUIET MOVE, from the plate to the ladder.
//
// Pressing a Stealth plate does not roll anything where it is pressed. It
// becomes a REQUEST (`QuietAsk`): who, which plate, how hard they have set
// it. The GM's screen turns that into a test - the plate's subject for the
// one acting, against the plate's answer from the SHARPEST of the
// opposition - rolls it as a secret called test, and reads what the result
// proposes for the ladder.
//
// Go loud is a request too, with nothing to roll.
//
// Pure. Zero React dependency.
// ============================================================
import { actionBeat } from '../attackBeat';
import { DIFFICULTY_LADDER } from '../characteristicTests';
import { subjectLabel } from '../narrative/callForTest';
import type { CalledResult, TestCall } from '../narrative/callForTest';
import type { ActionDef } from '../actions/types';
import type { Outcome } from './proposals';
import { NOISES, SIGNATURES } from './noise';
import type { Noise, Signature } from './noise';

/** The request's kind on the wire (an `asks` document addressed to the GM). */
export const QUIET_ASK = 'stealth';

export interface QuietAsk {
  /** The plate's id in the stealth catalog. */
  moveId: string;
  actorId: string;
  /** The one acting, by name. */
  name: string;
  /** The modifier they set on the notch for their own side. */
  difficulty: number;
  /** Whom they had targeted when they pressed it (Z1): where Observe looks, Distract lands, Shadow follows. */
  targetId?: string;
  /**
   * Present on the one request that is not a plate: an ATTACK made while
   * the table is in Stealth (`moveId` is then `ATTACK_HEARD`). What the
   * weapon sounded like and whether it landed - all the ladder needs.
   */
  /**
   * An attack the ladder heard: `noise` is how it was HEARD (loud when anybody listening was within reach), `step`
   * the weapon's own Signature when the coarse word does not say it (plan armoury) - absent on an older screen.
   */
  attack?: { noise: Noise; hit: boolean; step?: Signature };
}

/** The `moveId` of an attack's request. Not a plate: nothing in the catalog has this id. */
export const ATTACK_HEARD = 'attack';

const signed = (n: number) => (n >= 0 ? `+${n}` : String(n));

function difficultyText(mod: number): string {
  const step = DIFFICULTY_LADDER.find((s) => s.mod === mod);
  return step ? `${step.label} ${signed(mod)}` : signed(mod);
}

/** "Silent Move against Awareness", "Awareness" - what a plate tests, in words. Empty for a plate that tests nothing. */
export function quietTestLabel(def: ActionDef): string {
  if (!def.quiet) return '';
  const mine = subjectLabel(def.quiet.actor);
  return def.quiet.defender ? `${mine} against ${subjectLabel(def.quiet.defender)}` : mine;
}

/** The request as the GM reads it: "Castiel: Move unseen - Silent Move against Awareness (Hard -20)". */
export function quietAskLine(ask: QuietAsk, def: ActionDef): string {
  const test = quietTestLabel(def);
  if (!test) return `${ask.name}: ${def.name}`;
  return `${ask.name}: ${def.name} - ${test} (${difficultyText(ask.difficulty)})`;
}

/** The request an attack makes: who struck, with what kind of noise, and whether it landed. */
export function attackAskOf(
  actor: { id: string; name: string },
  noise: Noise,
  hit: boolean,
  step: Signature = noise,
): QuietAsk {
  // The step rides only when the heard word does not already say it, so a plain loud or silent ask is as it was.
  const attack = { noise, hit, ...(step !== noise ? { step } : {}) };
  return { moveId: ATTACK_HEARD, actorId: actor.id, name: actor.name, difficulty: 0, attack };
}

/**
 * An attack's request as the GM reads it: "Castiel attacked with a silent weapon, and missed"; "... with a muffled
 * weapon, out of anyone's hearing" when its reach fell short of every listener.
 */
export function attackLine(ask: QuietAsk): string {
  const heard = ask.attack?.noise ?? 'loud';
  const step = ask.attack?.step ?? heard;
  const unheard = heard === 'silent' && step !== 'silent' ? ", out of anyone's hearing" : '';
  return `${ask.name} attacked with a ${step} weapon${unheard}${ask.attack?.hit ? '' : ', and missed'}`;
}

export function quietAskOf(
  def: ActionDef,
  actor: { id: string; name: string },
  difficulty: number,
  targetId?: string | null,
): QuietAsk {
  const toward = targetId && targetId !== actor.id ? { targetId } : {};
  return { moveId: def.id, actorId: actor.id, name: actor.name, difficulty, ...toward };
}

/** The quiet moves that are the mover's alone: nothing they do is aimed at the one selected. */
const UNAIMED = new Set(['moveUnseen', 'hide']);

/** What each quiet move reads as in the story, of whoever made it. */
const QUIET_WORDS: Record<string, string> = {
  moveUnseen: 'moves quietly',
  hide: 'goes to ground',
  observe: 'watches',
  distract: 'makes a noise elsewhere',
  shadow: 'follows, quiet',
};

/**
 * A quiet move's PUBLIC line (plan design-gaps, Z1): every screen plays its gesture on the map - from the mover
 * toward whom they had targeted, else on the mover; Move unseen and Hide aim at nobody, so they are the mover's
 * whoever is selected (the Track Z review). Always "landed": how the roll went is the GM's secret, and the beat's
 * column is read by every screen. Null for a plate that is no quiet move (Go loud, an attack).
 */
export function quietBeatLine(ask: QuietAsk, def: ActionDef, at: number): { text: string; beat: string } | null {
  if (!def.quiet || !def.beat) return null;
  const words = QUIET_WORDS[def.id] ?? 'acts quietly';
  const toward = UNAIMED.has(def.id) ? ask.actorId : (ask.targetId ?? ask.actorId);
  const beat = actionBeat(ask.actorId, toward, { id: def.id, plays: def.beat.plays }, true, at);
  return { text: `${ask.name} ${words}.`, beat };
}

const text = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

function readAttack(raw: unknown): NonNullable<QuietAsk['attack']> | null {
  const stored = (raw ?? null) as { noise?: unknown; hit?: unknown; step?: unknown } | null;
  if (!stored || !NOISES.includes(stored.noise as Noise)) return null;
  const step = SIGNATURES.includes(stored.step as Signature) ? { step: stored.step as Signature } : {};
  return { noise: stored.noise as Noise, hit: stored.hit === true, ...step };
}

/** A request that crossed the wire, validated; null when it does not say who or which plate. */
export function readQuietAsk(raw: unknown): QuietAsk | null {
  const stored = (raw ?? {}) as Record<string, unknown>;
  const moveId = text(stored.moveId);
  const actorId = text(stored.actorId);
  if (!moveId || !actorId) return null;
  const difficulty =
    typeof stored.difficulty === 'number' && Number.isFinite(stored.difficulty) ? stored.difficulty : 0;
  const attack = readAttack(stored.attack);
  // An attack's request must say what was heard; a plate's must not claim to be one.
  if ((moveId === ATTACK_HEARD) !== (attack !== null)) return null;
  const targetId = text(stored.targetId);
  return {
    moveId,
    actorId,
    name: text(stored.name) ?? 'Someone',
    difficulty,
    ...(attack ? { attack } : {}),
    ...(targetId ? { targetId } : {}),
  };
}

/**
 * The test a request becomes: secret, the one acting on the plate's subject
 * at their difficulty, against the sharpest of the opposition on the
 * plate's answer at the GM's difficulty for them. With nobody there to
 * notice, or a plate nobody answers, the test stands alone. Null for a
 * plate that is not a test.
 */
export function quietCall(
  def: ActionDef,
  ask: QuietAsk,
  sharpestId: string | null,
  theirDifficulty: number,
): TestCall | null {
  if (!def.quiet) return null;
  const { actor, defender } = def.quiet;
  const against =
    defender && sharpestId && sharpestId !== ask.actorId
      ? { who: sharpestId, subject: defender, difficulty: theirDifficulty }
      : null;
  return { who: [ask.actorId], subject: actor, difficulty: ask.difficulty, against, secret: true };
}

/** What the ladder's proposal reads off a rolled result; null when nothing was rolled (a refusal). */
export function outcomeOf(result: CalledResult | undefined): Outcome | null {
  if (!result || result.passed === null) return null;
  return { passed: result.passed, margin: result.margin };
}
